import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Icon } from "./Icon";
import { ANIMATION_DURATIONS } from "../constants/animations";

interface HeaderSectionLabelProps {
  /** Current section id — the key the swap animates on. */
  sectionId?: string;
  /** Display label; empty for sections that don't have one (intro, TOC). */
  label: string;
  /** Font Awesome icon name (e.g. "fass chart-line"). */
  icon?: string;
}

/**
 * The header's section label, with a transition that marks a section change.
 *
 * This carries real information now: crossing a section boundary no longer
 * always shows an interstitial (see useSectionTransition), so this is the only
 * acknowledgement a viewer gets on a re-read or a backward scroll. It aims to
 * catch the eye without interrupting — a short pause, the outgoing label
 * leaving as the incoming one arrives, and a background emphasis that blooms
 * behind the new label and decays back to the plain header affordance.
 *
 * Reduced motion needs no special handling: the tree is wrapped in
 * `<MotionConfig reducedMotion="user">`, which drops the transforms and keeps
 * the opacity, leaving a plain crossfade.
 */
export function HeaderSectionLabel({
  sectionId,
  label,
  icon,
}: HeaderSectionLabelProps) {
  // The emphasis plays on *changes* only. Rendering it on mount would flash it
  // on page load and again every time the header remounts (it unmounts under
  // the interstitial), which is exactly when the section was just announced.
  const [emphasisFor, setEmphasisFor] = useState<string | null>(null);
  // Seeded from the prop so the first render has nothing to compare against
  // and stays quiet, without a separate "not yet set" sentinel to check.
  const previousSectionIdRef = useRef(sectionId);

  useEffect(() => {
    if (previousSectionIdRef.current !== sectionId) {
      setEmphasisFor(sectionId ?? null);
    }
    previousSectionIdRef.current = sectionId;
  }, [sectionId]);

  const showEmphasis = Boolean(label) && emphasisFor === sectionId;

  return (
    <h2
      // A section without a label renders an empty heading, and the icon is
      // decorative (aria-hidden), so there would be no accessible name. Keep
      // the box for layout, hide it from assistive tech.
      aria-hidden={!label}
      className="text-white/80 text-xs sm:text-lg mt-1 flex items-center justify-center"
      style={{
        fontFamily: "var(--font-narrative-body)",
        fontWeight:
          "var(--font-narrative-body-weight, 500)" as unknown as number,
      }}
    >
      {/* `mode="wait"` empties this box between labels, so the min-height keeps
          the header from collapsing a line mid-swap. */}
      <span className="relative inline-flex items-center justify-center min-h-[1.25rem] sm:min-h-[1.75rem]">
        {showEmphasis && (
          <motion.span
            key={`emphasis-${sectionId}`}
            aria-hidden="true"
            data-section-label-emphasis
            className="absolute -inset-x-3 -inset-y-1 rounded-full bg-white/[0.14] pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{
              duration: ANIMATION_DURATIONS.headerLabelEmphasis / 1000,
              times: [0, 0.18, 1],
              delay:
                (ANIMATION_DURATIONS.headerLabelSwapDelay +
                  ANIMATION_DURATIONS.headerLabelOut) /
                1000,
              ease: "easeOut",
            }}
          />
        )}

        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={sectionId ?? "no-section"}
            className="relative inline-flex items-center gap-1 whitespace-nowrap"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            // Exit carries its own timing: the delay is the "slight pause"
            // before the old label leaves, and `mode="wait"` holds the
            // incoming label until it finishes.
            exit={{
              opacity: 0,
              y: -6,
              transition: {
                delay: ANIMATION_DURATIONS.headerLabelSwapDelay / 1000,
                duration: ANIMATION_DURATIONS.headerLabelOut / 1000,
                ease: "easeIn",
              },
            }}
            transition={{
              duration: ANIMATION_DURATIONS.headerLabelIn / 1000,
              ease: "easeOut",
            }}
          >
            <Icon icon={icon} className="text-white/60" />
            {label}
          </motion.span>
        </AnimatePresence>
      </span>
    </h2>
  );
}
